<script setup lang="ts">
import TrackImportedFromTag from './TrackImportedFromTag.vue'

import TrackPreview from '~/components/track/preview.vue'
import TrackBadgeCharts from '~/components/track/TrackBadgeCharts.vue'
import TrackBadgeHype from '~/components/track/TrackBadgeHype.vue'

import { useTrackUpsells } from '~/composables/useTrackUpsells'

import { useUserBandStore } from '~/stores/userBand'

import type Track from '~/types/track'
import type { ThirdPartySource } from '~/types/track'

type Props = {
  track: Track
  bandName?: string
}

type Emits = {
  select: [id: Track['id']]
  'use-suggestion': [track: Track]
}

const props = withDefaults(defineProps<Props>(), {
  bandName: '',
})
const emit = defineEmits<Emits>()

const { BAND_DATA } = useUserBandStore()
const { hasHypeUpsell, hasChartsUpsell } = useTrackUpsells(props.track.upsells)

const trackSource = computed(
  () => props.track.generated_from as ThirdPartySource,
)

const isImported = computed(() => {
  const importSource = ['soundcloud', 'spotify']
  return importSource.includes(props.track.generated_from)
})

function handleClick(): void {
  if (props.track.id) emit('select', props.track.id)
  else emit('use-suggestion', props.track)
}
</script>

<template>
  <div
    class="trackSelectCardMainWrapper tw-relative tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-x-lg tw-rounded-xs tw-bg-surface tw-p-xl tw-shadow-round"
    data-test-id="trackSelectExistingCard"
    @click="handleClick"
    @keypress.enter="handleClick"
    @keypress.space="handleClick"
    role="button"
    tabindex="0"
  >
    <TrackPreview :track="track" class="!tw-w-[113px] tw-flex-shrink-0" />
    <div class="tw-flex tw-w-full tw-flex-col tw-overflow-hidden">
      <div class="tw-flex tw-items-center tw-gap-x-xs">
        <span class="tw-ellipsis tw-text-body tw-font-medium">
          {{ track.name }}
        </span>
        <div
          v-if="hasHypeUpsell || hasChartsUpsell"
          class="tw-flex tw-items-center tw-gap-x-2xs"
        >
          <TrackBadgeHype v-if="hasHypeUpsell" hide-hype-text />
          <TrackBadgeCharts
            v-if="hasChartsUpsell"
            :track-id="track.id"
            hide-charts-text
          />
        </div>
      </div>
      <span v-if="bandName.length" class="ellipsis tw-text-body-sm">
        {{ bandName }}
      </span>
      <span v-else class="ellipsis tw-text-body-sm">
        {{ BAND_DATA && BAND_DATA.name }}
      </span>
      <TrackImportedFromTag v-if="isImported" :track-source="trackSource" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.trackSelectCardMainWrapper:hover {
  box-shadow: var(--shadow-picture-dark);
}
</style>
